import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { GLReportingGroup, ClientApi, FileParameter, InstallerTrackingResource, GroupKeyMemberResourceWithOriginals, GLReportingGroupMember, CloneReportingGroupKeyResource } from 'src/app/client-api.service';

@Injectable({
  providedIn: 'root'
})
export class FinancialPlanningService {
  
  private currentGroupKey = new BehaviorSubject<string>('');

  constructor(private apiClient: ClientApi) { }

  private databaseSynced = new BehaviorSubject<boolean>(false);
  readonly databaseSynced$ = this.databaseSynced.asObservable();

  private dimDatePopulated = new BehaviorSubject<boolean>(false);
  readonly dimDatePopulated$ = this.dimDatePopulated.asObservable();
  
  private initialActBudSynced = new BehaviorSubject<boolean>(false);
  readonly initialActBudSynced$ = this.initialActBudSynced.asObservable();

  private installerTracking = new BehaviorSubject<InstallerTrackingResource[]>([]);
  readonly installerTracking$ = this.installerTracking.asObservable();
  
  cloneGLReportingGroup(cloneReportingGroupKeyResource: CloneReportingGroupKeyResource) {
    return this.apiClient.cloneReportingGroup(cloneReportingGroupKeyResource as CloneReportingGroupKeyResource);
  }

  deleteGLReportingGroupMember(groupKey: string, orgCode: string, propertyCode: string) {
    return this.apiClient.deleteReportingGroupMember(groupKey, orgCode, propertyCode);
  }

  refreshInstallerTrackingObservable() {
    this.setInstallerTracking();
  }

  setInstallerTracking() {
    this.databaseSynced.next(false);
    this.dimDatePopulated.next(false);
    this.initialActBudSynced.next(false);

    this.getInstallerTracking().pipe(
      map(installerTracking => {
        if(installerTracking.some(t => t.command === '-d')){
          this.databaseSynced.next(true);  // set flag here to to indicate -d was run
        }        
        if(installerTracking.some(t => t.command === '-dd')){
          this.dimDatePopulated.next(true);  // set flag here to to indicate -dd was run
        }        
        if(installerTracking.some(t => t.command === '-i')){
          this.initialActBudSynced.next(true);  // set flag here to to indicate -i was run
        }        
        return installerTracking;
      })
    ).subscribe(res => this.installerTracking.next(res));
  }
  
  getFPOrganizations() {
    return this.apiClient.getFPOrganizations();
  }

  fpInstaller(args: string[]) {
    return this.apiClient.fpInstaller(args);
  }

  fpInstallerStatus() {
    return this.apiClient.fpInstallerStatus();
  }

  getPayrollData() {
    return this.apiClient.getPayrollData();
  }

  getInstallerTracking() {
    return this.apiClient.getInstallerTracking();
  }

  insertGLReportingGroupMember(groupKeyMember: GLReportingGroupMember) {
    return this.apiClient.postReportingGroupMember(groupKeyMember);
  }

  postPayrollAllowanceFile(fileParameter: FileParameter) {
    return this.apiClient.installpayrollallowances(fileParameter);  
  }

  postPayrollWagesFile(fileParameter: FileParameter) {
    return this.apiClient.installpayrollwages(fileParameter);  
  }

  processFpReportingTables() {
    return this.apiClient.processFpReportingTables();
  }

  postReportingGroup(groupKey: string, name: string) {
    // const glReportingGroup = new GLReportingGroup();
    //       glReportingGroup.name = name;
    //       glReportingGroup.groupKey = groupKey;

    const glReportingGroup: GLReportingGroup = {
        name: name,
        groupKey: groupKey
    };     

    return this.apiClient.postReportingGroup(glReportingGroup);
  }

  /*
  * manage the communication between the GroupKey list and the Members Blade
  */

  setCurrentGroupKeySubscriptionValue(groupKey: string) {
    this.currentGroupKey.next( groupKey );
  }

  clearCurrentGroupKeySubscriptionValue() {
    this.currentGroupKey.next('');
  }

  getCurrentGroupKeySubscriptionValue() {
    return this.currentGroupKey.asObservable();
  }

    // service calls
  getReportingGroups() {
    return this.apiClient.getReportingGroups();
  }

  deleteGLReportingGroup(groupKey: string) {
    return this.apiClient.deleteReportingGroup(groupKey);
  }

  patchReportingGroupMembers(groupKeyMemberWithOriginals: GroupKeyMemberResourceWithOriginals[]) {
    return this.apiClient.patchReportingGroupMembers(groupKeyMemberWithOriginals as GroupKeyMemberResourceWithOriginals[]);
  }

}

export interface FPGroupRow {
  groupKey: string;
  name: string;
}

export interface GroupKey {
    groupKey?: string | undefined;
    name?: string | undefined;
}

export interface GroupKeyMember {
    groupKey?: string | undefined;
    orgCode?: string | undefined;
    propertyCode?: string | undefined;
}

export class WorkingGroupKeyMember {
    groupKey?: string | undefined;
    groupKeyNavigation?: GLReportingGroup;
    isDIrty: boolean | undefined;
    orgCode?: string | undefined;
    orgCodeInputElementId?: string | undefined;
    originalOrgCode?: string | undefined;
    originalPropertyCode?: string | undefined;
    propertyCode?: string | undefined;
    propertyCodeInputElementId?: string | undefined;

    constructor() {}
}
